import type { MCampaignPlacement, MCreative, MMedium, MMediumUpload, MTemplate } from "@/types";

type VisualStatus = "built" | "outdated" | "missing";

interface CreativeVisual {
  key: string;
  template: MTemplate;
  upload: MMediumUpload;
  assetLabel: string;
  url: string | null;
  status: VisualStatus;
}

const TITLE_KEY = /^title(\d*)$/;
const VIDEO_TEMPLATE_NAME = "blank";

const titleRank = (key: string) => Number(key.match(TITLE_KEY)?.[1] || 0);

const escapeRegExp = (value: string) => value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

export const creativeTemplates = (creative: Pick<MCreative, "template_ids">, templates: MTemplate[]) =>
  templates.filter((template) => creative.template_ids?.includes(template.id));

export const creativeUploads = (creative: Pick<MCreative, "m_medium_upload_ids">, uploads: MMediumUpload[]) =>
  uploads.filter((upload) => creative.m_medium_upload_ids?.includes(upload.id));

export const creativeThumbnail = (creative: MCreative, uploads: MMediumUpload[]) => {
  const upload = uploads.find((candidate) => candidate.id === creative.m_medium_upload_ids[0]);
  return upload ? { url: upload.url, mediaType: upload.media_type } : null;
};

export const creativeHeadline = (creative: MCreative, templates: MTemplate[]) => {
  const templateDefaults: Record<string, string> = {};
  creativeTemplates(creative, templates).forEach((template) => {
    Object.entries(template.m_template_attributes ?? {}).forEach(([key, attribute]) => {
      if (!(key in templateDefaults)) templateDefaults[key] = attribute?.default ?? "";
    });
  });

  const creativeValues = creative.m_template_attributes ?? {};
  const keys = Object.keys(creative.template_ids.length ? templateDefaults : creativeValues);

  return keys
    .filter((key) => TITLE_KEY.test(key))
    .sort((a, b) => titleRank(a) - titleRank(b))
    .map((key) => (creativeValues[key]?.trim() || templateDefaults[key]?.trim()) ?? "")
    .filter(Boolean)
    .join(" / ");
};

// Longest first, so @age_min is not read as @age followed by "_min".
const adSetVariablePattern = (adSetVariables: string[]) =>
  new RegExp([...adSetVariables].sort((a, b) => b.length - a.length).map(escapeRegExp).join("|"));

// The API renders ad set variables (@geo, @age...) into the medium's attributes,
// so they stand for any text when comparing with the creative's raw attributes.
const attributeMatches = (builtValue: string, currentValue: string | undefined, adSetVariables: string[]) => {
  if (builtValue === currentValue) return true;
  if (!currentValue || !adSetVariables.length) return false;

  const variablePattern = adSetVariablePattern(adSetVariables);
  if (!variablePattern.test(currentValue)) return false;

  const pattern = currentValue.split(variablePattern).map(escapeRegExp).join(".*");
  return new RegExp(`^${pattern}$`, "s").test(builtValue);
};

const matchesAttributes = (
  mMedium: MMedium,
  currentAttributes: Record<string, string> | null,
  adSetVariables: string[],
) =>
  Object.entries(mMedium.m_template_attributes ?? {}).every(([key, value]) =>
    attributeMatches(value, currentAttributes?.[key], adSetVariables),
  );

// A creative keeps one m_medium per (placement, asset, template, m_template_attributes) combination,
// so editing a title leaves older m_media behind. Rank candidates so the preview shows the one matching
// the creative's current attributes, then the active one, then the most recent.
export const pickMMedium = (
  candidates: MMedium[],
  currentAttributes: Record<string, string> | null,
  adSetVariables: string[],
) => {
  const score = (mMedium: MMedium) =>
    (matchesAttributes(mMedium, currentAttributes, adSetVariables) ? 2 : 0) + (mMedium.active ? 1 : 0);

  return candidates.reduce<MMedium | null>((best, mMedium) => {
    if (!best) return mMedium;
    const [mMediumScore, bestScore] = [score(mMedium), score(best)];
    if (mMediumScore !== bestScore) return mMediumScore > bestScore ? mMedium : best;
    return (mMedium.id ?? 0) > (best.id ?? 0) ? mMedium : best;
  }, null);
};

export const findMMedium = (
  creative: Pick<MCreative, "m_media" | "m_template_attributes">,
  placement: MCampaignPlacement,
  uploadId: number,
  templateId: number,
  adSetVariables: string[],
) =>
  pickMMedium(
    (creative.m_media ?? []).filter(
      (candidate) =>
        candidate.placement === placement &&
        candidate.m_medium_upload_id === uploadId &&
        candidate.m_template_id === templateId,
    ),
    creative.m_template_attributes,
    adSetVariables,
  );

const visualStatus = (mMedium: MMedium | null, creative: MCreative, adSetVariables: string[]): VisualStatus => {
  if (!mMedium?.url) return "missing";
  return matchesAttributes(mMedium, creative.m_template_attributes, adSetVariables) ? "built" : "outdated";
};

// Mirrors Ads::Ops::CBuilder: an image creative renders every (template, image) pair,
// a video creative renders its first video of the placement with the blank template.
const placementSources = (
  creative: MCreative,
  placement: MCampaignPlacement,
  uploads: MMediumUpload[],
  templates: MTemplate[],
) => {
  const selectedUploads = creativeUploads(creative, uploads);

  if (creative.media_type === "video") {
    const video = selectedUploads.find((upload) => upload.media_type === "video" && upload.placement === placement);
    const blankTemplate = templates.find((template) => template.name === VIDEO_TEMPLATE_NAME);
    return { templates: blankTemplate ? [blankTemplate] : [], uploads: video ? [video] : [] };
  }

  return { templates: creativeTemplates(creative, templates), uploads: selectedUploads };
};

export const creativeVisuals = (
  creative: MCreative,
  placement: MCampaignPlacement,
  uploads: MMediumUpload[],
  templates: MTemplate[],
  adSetVariables: string[],
): CreativeVisual[] => {
  if (!creative.placements?.[placement]) return [];

  const sources = placementSources(creative, placement, uploads, templates);
  const fallbackLabel = creative.media_type === "video" ? "Video" : "Image";

  return sources.templates.flatMap((template) =>
    sources.uploads.map((upload, index) => {
      const mMedium = findMMedium(creative, placement, upload.id, template.id, adSetVariables);

      return {
        key: `${template.id}-${upload.id}`,
        template,
        upload,
        assetLabel: upload.name || `${fallbackLabel} ${index + 1}`,
        url: mMedium?.url || upload.url,
        status: visualStatus(mMedium, creative, adSetVariables),
      };
    }),
  );
};
